import React, { FC, isValidElement, useLayoutEffect, useRef, useState } from 'react';

import styled from 'styled-components';

import { OnClick } from '@rbi-ctg/frontend';

import { SlidingUnderline, TabStyle } from './styled';
import { IActive, ITabsStyle } from './styled/types';
import { ITabs } from './types';

export const TabsStyle = styled.div<ITabsStyle>`
  display: flex;
  flex-direction: row;
  width: ${p => (p.tabWidth === 'fill' ? '100%' : 'auto')};
  margin: 0;
  padding: 0;
`;

export const TabContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SecondaryInfo = styled.p`
  margin: 0;
  font-weight: ${Styles.fontWeight.heavy};
`;

export const Tabs: FC<ITabs & ITabsStyle> = ({
  children,
  color,
  inactiveStyle,
  onChange,
  selectedIndex,
  slidingUnderline = false,
  slidingUnderlineColor,
  tabWidth = 'fill',
}) => {
  const container = useRef<null | HTMLElement>();
  const [containerWidth, setContainerWidth] = useState(0);
  const enableSlidingUnderline = tabWidth === 'fill' && slidingUnderline;

  useLayoutEffect(() => {
    if (enableSlidingUnderline) {
      setContainerWidth(container.current?.getBoundingClientRect()?.width ?? 0);
    }
  }, [enableSlidingUnderline]);

  if (!children || children.length <= 0) {
    return null;
  }
  const tabSlideWidth = containerWidth / children.length;

  const tabs = children.map(child => {
    if (!isValidElement(child)) {
      return child;
    }

    const handleClick = (e: React.ChangeEvent<{}>) => {
      onChange(e, child.props.value);
    };

    const isSelected = child.props.value === selectedIndex;

    return React.cloneElement(child, {
      color,
      id: child.props.id,
      inactiveStyle,
      key: `tab_${child.props.value}`,
      active: !enableSlidingUnderline && isSelected,
      'aria-selected': isSelected,
      tabIndex: isSelected ? 0 : -1,
      ariaControls: child.props.ariaControls,
      onClick: handleClick,
      tabWidth: child.props.tabWidth || tabWidth,
    });
  });

  return (
    <TabContainer
      ref={ref => {
        if (ref) {
          container.current = ref;
        }
      }}
    >
      <TabsStyle tabWidth={tabWidth} role="tablist">
        {tabs}
      </TabsStyle>
      {enableSlidingUnderline && (
        <SlidingUnderline
          tabSlideWidth={tabSlideWidth}
          idx={selectedIndex}
          color={slidingUnderlineColor || color}
        />
      )}
    </TabContainer>
  );
};

interface ITab {
  id?: string;
  'data-testid'?: string;
  inactiveStyle?: { [key: string]: string | number };
  active?: boolean;
  label: string;
  value?: number;
  onClick?: OnClick;
  ariaControls?: string;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'inverted';
}

export const Tab: FC<ITab & ITabsStyle & { className?: string }> = ({
  id,
  'data-testid': dataTestId,
  'aria-selected': ariaSelected,
  label,
  active,
  disabled,
  color,
  onClick,
  ariaControls,
  inactiveStyle,
  tabWidth,
  tabIndex,
  className,
  boldText = false,
  secondaryInfo,
}) => {
  const accessibleName = secondaryInfo ? `${label} ${secondaryInfo}` : label;
  const selected = ariaSelected ?? active;

  return (
    // @ts-ignore - styled-components v5 types don't expose native HTML props
    <TabStyle
      id={id}
      data-testid={dataTestId}
      role="tab"
      aria-selected={selected}
      aria-controls={ariaControls}
      aria-label={accessibleName}
      color={color}
      disabled={disabled}
      active={active}
      onClick={onClick}
      inactiveStyle={inactiveStyle}
      tabWidth={tabWidth}
      tabIndex={tabIndex}
      className={className}
      boldText={boldText}
      secondaryInfo={secondaryInfo}
    >
      {label}
      {secondaryInfo && <SecondaryInfo>{secondaryInfo}</SecondaryInfo>}
    </TabStyle>
  );
};

interface ITabPanel {
  id: string;
  value: number;
  index?: number;
  children: React.ReactNode;
  ariaLabeledBy: string;
}

const TabPanelStyle = styled.div<IActive>`
  display: ${p => (p.active ? 'flex' : 'none')};
  position: relative;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
  :focus {
    outline: 0;
  }
  flex: 1 1 auto;
`;

export const TabPanel: FC<ITabPanel> = ({ id, value, index, children, ariaLabeledBy }) => {
  const hidden = value !== index;
  return (
    <TabPanelStyle
      id={id}
      key={`tab-panel_${id}`}
      hidden={hidden}
      active={!hidden}
      role="tabpanel"
      tabIndex={hidden ? undefined : -1}
      aria-labelledby={ariaLabeledBy}
    >
      {!hidden && children}
    </TabPanelStyle>
  );
};
